<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-area-select-modal"
      dialog-width="100vw"
      dialog-height="100vh"
    >
      <template #header>
        <BaseIcon name="mdi/scissors-cutting" />
        {{ ls({ ja: '範囲の選択', en: 'Select Area' }) }}
      </template>

      <template #header-extra>
        <div class="layout-row area-select-modal-controllers">
          <div class="area-select-controls">
            <BaseSlider
              class="size-slider"
              v-model="size"
              :min="10"
              :max="200"
              @update:model-value="draw()"
            />

            <div class="controls-select layout-row">
              <BaseSelect
                class="jps-select"
                v-model="clipType"
                :options="clipTypes"
                :label-function="c => ls(c.label)"
                :value-function="c => c.value"
              />
              <button class="jps-button" type="button" @click="clear()">
                {{ lss('範囲をクリア', 'Clear selection') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <div class="jps-modal-content">
        <canvas
          :style="canvasStyle"
          ref="canvas"
          class="drawing"
          width="0"
          height="0"
        />
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()">{{ lss('選択範囲で切り取る', 'Crop to selection') }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

interface Anchor {
  cursor: string
  x?: boolean
  y?: boolean
  w?: boolean
  h?: boolean
  fixx?: number
  fixy?: number
  fixw?: number
  fixh?: number
  coordx: number
  coordy: number
}
interface Point {
  x
  y
}

const { ls, lss } = useI18n()

interface Props {
  src?: string,
  rect?: Rect,
  ruleField?: ValidationRuleField
}
const props = withDefaults(defineProps<Props>(),{
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _src: Ref<string> = ref()
const _rect: Ref<Rect> = ref()
const _ruleField: Ref<ValidationRuleField> = ref()
const canvas: Ref<HTMLCanvasElement> = ref()
const context: Ref<CanvasRenderingContext2D> = ref()
const imageObj: Ref<HTMLImageElement> = ref()
let init: boolean = false
const openAwait = async (options?: Props) => {
  init = false
  setOptionsValue(_src, 'src', props, options)
  setOptionsValue(_rect, 'rect', props, options)
  setOptionsValue(_ruleField, 'ruleField', props, options)

  clipType.value = '16:9'
  innerRect.value = null
  tempRect.value = null
  size.value = 100

  currentAnchor.value = null
  basePoint.value = null
  onAnchor.value = ''
  onRect.value = false

  initCanvas()
  return await modal.value.openAwait<Rect>()
}
defineExpose({ openAwait })

const confirm = () => {
  document.removeEventListener('mouseup', onMouseUp, false)
  document.removeEventListener('touchend', onMouseUp, false)
  document.removeEventListener('mousemove', onMouseMove, false)
  document.removeEventListener('touchmove', onMouseMove, false)
  modal.value.resolve(innerRect.value)
}

const initCanvas = () => {
  nextTick(() => {
    if (init || !canvas.value) {
      return
    }
    init = true
    context.value = canvas.value.getContext('2d')
    canvas.value.addEventListener('mousedown', onMouseDown, false)
    canvas.value.addEventListener('touchstart', onMouseDown, false)
    imageObj.value = new Image()
    imageObj.value.onload = () => {
      baseScale.value = 800.0 / imageObj.value.width
      draw()
    }
    innerRect.value = {
      x: _rect.value.x,
      y: _rect.value.y,
      w: _rect.value.w,
      h: _rect.value.h,
    }
    context.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    imageObj.value.src = _src.value
  })
}

watch(
  () => canvas.value,
  () => initCanvas(),
)
watch(
  () => _src.value,
  () => initCanvas(),
)

//data
const clipType: Ref<string> = ref('16:9')
const innerRect: Ref<Rect> = ref(null)
const tempRect: Ref<Rect> = ref(null)
const size: Ref<number> = ref(100)
const baseScale = ref(1.0)

const clipTypes = computed(() => {
  let types = []
  types.push(
    { label: { ja: 'ワイド（16:9）', en: 'Wide (16:9)' }, value: '16:9' },
    { label: { ja: '正方形', en: 'Square' }, value: '1:1' },
    { label: { ja: '自由な比率', en: 'Free' }, value: 'free' }
  )
  if (_ruleField.value) {
    types = types.filter((t) => t.value !== _ruleField.value.imageRatio)
    if (_ruleField.value && _ruleField.value.imageRatio) {
      types.unshift({
        label: {
          ja: `推奨(${_ruleField.value.imageRatio})`,
          en: `Preffered(${_ruleField.value.imageRatio})`,
        },
        value: _ruleField.value.imageRatio,
      })
      clipType.value = _ruleField.value.imageRatio
    }
  }
  return types
})

const eventPoint = (evt): Point => {
  const coord = evt.changedTouches ? evt.changedTouches[0] : evt
  const canvCoord = canvas.value.getBoundingClientRect()
  return {
    x: coord.clientX - canvCoord.left,
    y: coord.clientY - canvCoord.top,
  }
}

const anchors = computed(() => {
  if (!innerRect.value) return []
  return [
    {
      x: true,
      y: true,
      coordx: innerRect.value.x,
      coordy: innerRect.value.y,
      cursor: 'nw-resize',
    },
    {
      y: true,
      coordx: innerRect.value.x + innerRect.value.w / 2,
      coordy: innerRect.value.y,
      cursor: 'n-resize',
    },
    {
      w: true,
      y: true,
      coordx: innerRect.value.x + innerRect.value.w,
      coordy: innerRect.value.y,
      cursor: 'ne-resize',
    },
    {
      w: true,
      coordx: innerRect.value.x + innerRect.value.w,
      coordy: innerRect.value.y + innerRect.value.h / 2,
      cursor: 'e-resize',
    },
    {
      w: true,
      h: true,
      coordx: innerRect.value.x + innerRect.value.w,
      coordy: innerRect.value.y + innerRect.value.h,
      cursor: 'se-resize',
    },
    {
      h: true,
      coordx: innerRect.value.x + innerRect.value.w / 2,
      coordy: innerRect.value.y + innerRect.value.h,
      cursor: 's-resize',
    },
    {
      x: true,
      h: true,
      coordx: innerRect.value.x,
      coordy: innerRect.value.y + innerRect.value.h,
      cursor: 'sw-resize',
    },
    {
      x: true,
      coordx: innerRect.value.x,
      coordy: innerRect.value.y + innerRect.value.h / 2,
      cursor: 'w-resize',
    },
  ]
})

const currentAnchor: Ref<Anchor> = ref(null)

const inside = (r: Rect, p: Point) => {
  return r.x < p.x && r.x + r.w > p.x && r.y < p.y && r.y + r.h > p.y
}

const basePoint: Ref<Point> = ref(null)

const fixRatio = (rect: Rect) => {
  if (clipType.value === 'free') return
  let ratioNum = clipType.value.split(':').map((r) => Number(r))
  if (ratioNum[0] == ratioNum[1]) {
    rect.w = rect.h = Math.max(rect.w, rect.h)
  } else if (ratioNum[0] >= ratioNum[1]) {
    rect.h = (rect.w * ratioNum[1]) / ratioNum[0]
  } else {
    rect.w = (rect.h * ratioNum[1]) / ratioNum[0]
  }
}

const onMouseDown = (e: MouseEvent) => {
  let p = eventPoint(e)
  if (!tempRect.value) {
    currentAnchor.value = anchors.value
      .filter((a) => {
        let r = anchorToRect(a)
        return inside(r, p)
      })
      .pop()
    if (currentAnchor.value) {
      tempRect.value = {
        x: toCanvasValue(innerRect.value.x),
        y: toCanvasValue(innerRect.value.y),
        w: toCanvasValue(innerRect.value.w),
        h: toCanvasValue(innerRect.value.h),
      }
    } else if (
      innerRect.value &&
      innerRect.value.w != null &&
      inside(toCanvasRect(innerRect.value), p)
    ) {
      let r = toCanvasRect(innerRect.value)
      basePoint.value = {
        x: r.x - p.x,
        y: r.y - p.y,
      }
    } else {
      tempRect.value = { x: 0, y: 0, w: 0, h: 0 }
      tempRect.value.x = p.x
      tempRect.value.y = p.y
    }
    e.preventDefault()
    document.addEventListener('mouseup', onMouseUp, false)
    document.addEventListener('touchend', onMouseUp, false)
    document.addEventListener('mousemove', onMouseMove, false)
    document.addEventListener('touchmove', onMouseMove, false)
    draw()
  }
}

const onMouseMove =  (e: Event) => {
  let p = eventPoint(e)
  if (basePoint.value) {
    let pp = toActualPoint({
      x: basePoint.value.x + p.x,
      y: basePoint.value.y + p.y,
    })
    innerRect.value.x = pp.x
    innerRect.value.y = pp.y
    draw()
    e.preventDefault()
  } else if (tempRect.value) {
    if (currentAnchor.value) {
      if (currentAnchor.value.x) {
        tempRect.value.x = p.x
        tempRect.value.w =
          toCanvasValue(innerRect.value.w + innerRect.value.x) - p.x
      }
      if (currentAnchor.value.y) {
        tempRect.value.y = p.y
        tempRect.value.h =
          toCanvasValue(innerRect.value.h + innerRect.value.y) - p.y
      }
      if (currentAnchor.value.w) {
        tempRect.value.w = p.x - tempRect.value.x
      }
      if (currentAnchor.value.h) {
        tempRect.value.h = p.y - tempRect.value.y
      }
    } else {
      tempRect.value.w = p.x - tempRect.value.x
      tempRect.value.h = p.y - tempRect.value.y
    }
    fixRatio(tempRect.value)
    draw()
    e.preventDefault()
  } else {
    let _inside = anchors.value.filter((a) => {
      let r = anchorToRect(a)
      return inside(r, p)
    })
    if (_inside.length > 0) onAnchor.value = _inside[0].cursor
    else onAnchor.value = null
    onRect.value =
      !onAnchor.value &&
      innerRect.value != null &&
      innerRect.value.x != null &&
      inside(toCanvasRect(innerRect.value), p)
  }
}

const canvasStyle = computed(() => {
  if (onAnchor.value) return { cursor: onAnchor.value }
  if (onRect.value) return { cursor: 'move' }
})

const onAnchor: Ref<string> = ref('')
const onRect: Ref<boolean> = ref(false)

const onMouseUp = (e: MouseEvent) => {
  if (basePoint.value) {
    basePoint.value = null
  } else if (tempRect.value) {
    if (tempRect.value.w <= 5 || tempRect.value.h <= 5) {
      innerRect.value = null
    } else {
      innerRect.value = modRect(
        tempRect.value,
        100 / size.value / baseScale.value
      )
    }
    tempRect.value = null
    document.removeEventListener('mouseup', onMouseUp, false)
    document.removeEventListener('touchend', onMouseUp, false)
    document.removeEventListener('mousemove', onMouseMove, false)
    document.removeEventListener('touchmove', onMouseMove, false)
    e.preventDefault()
    draw()
  }
}

const toCanvasRect = (r: Rect) => {
  return modRect(r, (baseScale.value * size.value) / 100)
}

const toCanvasValue = (x) => {
  return (x / 100) * size.value * baseScale.value
}

const toActualPoint = (p: Point) => {
  return { x: toActualValue(p.x), y: toActualValue(p.y) }
}

const toActualValue = (x) => {
  return (x * 100) / size.value / baseScale.value
}

const modRect = (r: Rect, mod): Rect => {
  return {
    x: Math.floor((r.w < 0 ? r.x + r.w : r.x) * mod),
    y: Math.floor((r.h < 0 ? r.y + r.h : r.y) * mod),
    w: Math.floor(Math.abs(r.w) * mod),
    h: Math.floor(Math.abs(r.h) * mod),
  }
}

const clear = () => {
  innerRect.value = {
    x: undefined,
    y: undefined,
    w: undefined,
    h: undefined,
  }
  draw()
}

const draw = () => {
  if (imageObj.value && imageObj.value.src) {
    canvas.value.width =
      ((imageObj.value.width * size.value) / 100.0) * baseScale.value
    canvas.value.height =
      ((imageObj.value.height * size.value) / 100.0) * baseScale.value
    context.value.drawImage(
      imageObj.value,
      0,
      0,
      imageObj.value.width,
      imageObj.value.height,
      0,
      0,
      canvas.value.width,
      canvas.value.height
    )
    if (tempRect.value != null) {
      drawGrayRect(tempRect.value, 1)
    } else if (innerRect.value != null && innerRect.value.x != null) {
      drawGrayRect(
        innerRect.value,
        (size.value / 100.0) * baseScale.value,
        true
      )
    }
  }
}

const anchorSize: number = 15
const anchorToRect = (a: Anchor): Rect => {
  return {
    x: toCanvasValue(a.coordx) - anchorSize / 2,
    y: toCanvasValue(a.coordy) - anchorSize / 2,
    w: anchorSize,
    h: anchorSize,
  }
}

const drawGrayRect = (innerRect: Rect, modsize, cut?: boolean) => {
  innerRect = modRect(innerRect, modsize)
  context.value.globalAlpha = 0.7
  context.value.lineWidth = 1
  context.value.strokeStyle = 'rgb(211, 12, 47)'
  context.value.beginPath()
  context.value.rect(innerRect.x, innerRect.y, innerRect.w, innerRect.h)
  context.value.stroke()

  if (cut) {
    //周辺の黒
    let fullw = canvas.value.width
    let fullh = canvas.value.height

    context.value.fillStyle = 'rgb(0,0,0)'
    context.value.fillRect(0, 0, fullw, innerRect.y)
    context.value.fillRect(0, innerRect.y, innerRect.x, innerRect.h)
    context.value.fillRect(
      innerRect.x + innerRect.w,
      innerRect.y,
      fullw - innerRect.x - innerRect.w,
      innerRect.h
    )
    context.value.fillRect(
      0,
      innerRect.y + innerRect.h,
      fullw,
      fullh - innerRect.y - innerRect.h
    )

    //アンカーポイント
    context.value.globalAlpha = 1
    context.value.lineWidth = 2
    context.value.strokeStyle = 'rgba(25, 106, 201)'
    anchors.value.forEach((a) => {
      context.value.beginPath()
      let r = anchorToRect(a)
      context.value.rect(r.x, r.y, r.w, r.h)
      context.value.stroke()
    })
  }
  context.value.globalAlpha = 1.0
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-area-select-modal {
  .drawing {
    border: solid 1px;
  }
  .jps-modal-content {
    background: $jps-gray-300;
    overflow: scroll;
    width: 100%;
    height: 100%;
  }

  .area-select-modal-controllers {
    padding: 0 1rem 1rem 1rem;
  }

  .area-select-controls {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 0.5rem 1.5rem;

    .size-slider {
      max-width: 14rem;
    }

    .controls-select {
      flex-shrink: 0;
      gap: 0.5rem;
    }
  }

  .size-canvas {
    position: relative;
    .size-change {
      position: absolute;
      top: 10px;
      left: 10px;
      width: 20px;
      height: 20px;
      z-index: 1000;
      border: 1px solid blue;
    }
  }
}
</style>
